import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Loader2, Zap, Plus, TrendingUp, AlertTriangle,
  CreditCard, XCircle, Minus,
} from "lucide-react";

const MAX_EXTRA_BLINGS = 20;

const brl = (v) => (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/* Small controlled input used in the checkout modal. */
function Field({ label, value, onChange, placeholder, required, testId, autoComplete }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-black text-slate-600">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </span>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        data-testid={testId}
        className="mt-1 w-full px-3 py-2 text-sm border-2 border-slate-200 focus:border-emerald-500 rounded-sm outline-none transition-colors"
      />
    </label>
  );
}

/* Modal that collects the minimum payer data Stripe Checkout needs. */
function CheckoutModal({ open, onClose, onSubmit, submitting, defaults, basePriceBrl, extraBlingBrl }) {
  const [form, setForm] = useState({
    name: "", email: "", cpf_cnpj: "", mobile_phone: "",
    postal_code: "", address: "", address_number: "",
    province: "", city: "", complement: "",
  });
  const [extraBlings, setExtraBlings] = useState(0);

  useEffect(() => {
    if (open && defaults) setForm((f) => ({ ...f, ...defaults }));
    if (open) setExtraBlings(0);
  }, [open, defaults]);

  if (!open) return null;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = form.name && form.email && form.cpf_cnpj && form.mobile_phone
    && form.postal_code && form.address && form.address_number && form.province && form.city;

  const clampedExtras = Math.max(0, Math.min(MAX_EXTRA_BLINGS, Number(extraBlings) || 0));
  const monthlyTotal = (basePriceBrl || 0) + clampedExtras * (extraBlingBrl || 0);
  const totalBlingLimit = 1 + clampedExtras;

  const decExtras = () => setExtraBlings((n) => Math.max(0, (Number(n) || 0) - 1));
  const incExtras = () => setExtraBlings((n) => Math.min(MAX_EXTRA_BLINGS, (Number(n) || 0) + 1));

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="checkout-modal"
    >
      <div className="bg-white rounded-xl border-2 border-slate-200 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Assinatura Vyntric</div>
            <h2 className="text-2xl font-black tracking-tighter">Configure seu plano</h2>
            <p className="text-xs text-slate-500 mt-1">
              Ajuste a quantidade de ERPs Bling e revise o valor antes do pagamento.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="checkout-modal-close">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <form
          className="p-6 space-y-5"
          onSubmit={(e) => { e.preventDefault(); if (canSubmit) onSubmit({ ...form, extra_blings: clampedExtras }); }}
        >
          {/* Extra Blings stepper */}
          <div
            className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-lg p-4"
            data-testid="extra-blings-selector"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="text-[10px] uppercase tracking-widest font-black text-emerald-700">
                  ERPs Bling adicionais
                </div>
                <div className="text-sm text-slate-700 mt-1">
                  Seu plano já inclui <b>1 Bling</b>. Contrate mais desde já
                  se você opera com múltiplos CNPJs.
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {brl(extraBlingBrl)} por Bling extra / mês · máx {MAX_EXTRA_BLINGS}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={decExtras}
                  disabled={clampedExtras === 0}
                  className="w-9 h-9 flex items-center justify-center border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
                  data-testid="extra-blings-decrement"
                  aria-label="Diminuir Blings extras"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min={0}
                  max={MAX_EXTRA_BLINGS}
                  value={clampedExtras}
                  onChange={(e) => setExtraBlings(e.target.value)}
                  className="w-16 h-9 text-center text-lg font-black border-2 border-emerald-200 focus:border-emerald-500 rounded-sm outline-none"
                  data-testid="extra-blings-input"
                />
                <button
                  type="button"
                  onClick={incExtras}
                  disabled={clampedExtras >= MAX_EXTRA_BLINGS}
                  className="w-9 h-9 flex items-center justify-center border-2 border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
                  data-testid="extra-blings-increment"
                  aria-label="Aumentar Blings extras"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Live total */}
            <div className="mt-4 pt-3 border-t border-emerald-200 flex items-end justify-between gap-4 flex-wrap">
              <div className="text-[11px] text-slate-600 leading-relaxed">
                {brl(basePriceBrl)} base
                {clampedExtras > 0 && (
                  <> {" + "} {clampedExtras} Bling{clampedExtras > 1 ? "s" : ""} extra × {brl(extraBlingBrl)}</>
                )}
                <br />
                Você terá até <b data-testid="extra-blings-total-limit">{totalBlingLimit}</b> ERP{totalBlingLimit > 1 ? "s" : ""} Bling conectado{totalBlingLimit > 1 ? "s" : ""}.
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total mensal</div>
                <div className="text-3xl font-black tracking-tighter text-emerald-700" data-testid="checkout-total">
                  {brl(monthlyTotal)}
                  <span className="text-xs text-slate-500 font-normal">/mês</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-600 mb-2">
              Dados do pagador
            </div>
            <p className="text-[11px] text-slate-500 -mt-1 mb-3">
              Você será redirecionado ao checkout seguro do Stripe para inserir o cartão de crédito.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Nome / Razão Social" value={form.name} onChange={set("name")}
                     required testId="checkout-name" autoComplete="name" />
              <Field label="E-mail" value={form.email} onChange={set("email")}
                     required testId="checkout-email" autoComplete="email" />
              <Field label="CPF ou CNPJ" value={form.cpf_cnpj} onChange={set("cpf_cnpj")}
                     required testId="checkout-cpf-cnpj" placeholder="somente números" />
              <Field label="Celular (com DDD)" value={form.mobile_phone} onChange={set("mobile_phone")}
                     required testId="checkout-phone" placeholder="somente números" autoComplete="tel" />
              <Field label="CEP" value={form.postal_code} onChange={set("postal_code")}
                     required testId="checkout-cep" autoComplete="postal-code" />
              <Field label="Endereço" value={form.address} onChange={set("address")}
                     required testId="checkout-address" autoComplete="street-address" />
              <Field label="Número" value={form.address_number} onChange={set("address_number")}
                     required testId="checkout-number" />
              <Field label="Complemento" value={form.complement} onChange={set("complement")}
                     testId="checkout-complement" />
              <Field label="Bairro" value={form.province} onChange={set("province")}
                     required testId="checkout-province" />
              <Field label="Cidade" value={form.city} onChange={set("city")}
                     required testId="checkout-city" />
            </div>
          </div>
          <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
            Ao continuar, você será cobrado(a) mensalmente no cartão informado no Stripe.
            Você pode cancelar a qualquer momento pela tela &quot;Minha Conta&quot;.
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-black uppercase tracking-wider border-2 border-slate-200 rounded-sm hover:bg-slate-50"
              data-testid="checkout-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="px-4 py-2 text-xs font-black uppercase tracking-wider border-2 border-emerald-600 bg-emerald-600 text-white rounded-sm inline-flex items-center gap-2 disabled:opacity-60 hover:bg-emerald-700"
              data-testid="checkout-submit"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
              Pagar {brl(monthlyTotal)}/mês
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MyBilling() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchSub = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription");
      setSub(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSub(); }, []);

  // Surface Stripe checkout return status via query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Accept ``checkout=`` (Stripe path, current) or ``status=`` (legacy Asaas)
    const status = params.get("checkout") || params.get("status");
    if (!status) return;
    const map = {
      success: ["success", "Pagamento confirmado — assinatura ativa em instantes."],
      cancelled: ["info", "Checkout cancelado. Você pode tentar novamente quando quiser."],
      expired: ["warning", "O link do checkout expirou. Gere um novo."],
    };
    const entry = map[status];
    if (entry) {
      const fn = entry[0] === "success" ? toast.success
        : entry[0] === "warning" ? toast.warning : toast.info;
      fn(entry[1]);
      // Clean the URL — keep tab=billing so we stay on this tab
      const cleanParams = new URLSearchParams();
      const tab = params.get("tab");
      if (tab) cleanParams.set("tab", tab);
      const suffix = cleanParams.toString() ? `?${cleanParams.toString()}` : "";
      window.history.replaceState({}, "", window.location.pathname + suffix);
      // Re-fetch subscription state — the webhook may take a beat to
      // process the payment even though the redirect happened.
      setTimeout(() => fetchSub(), 1500);
    }
  }, []);

  const buyExtraBling = async () => {
    if (!window.confirm(`Adicionar 1 Bling extra (+R$ 49,90/mês)?`)) return;
    setAdding(true);
    try {
      const { data } = await api.post("/subscription/purchase-extra-bling", { quantity: 1 });
      setSub((s) => ({ ...s, ...data, used_blings: s?.used_blings || 0 }));
      toast.success("Slot adicional contratado. Ajuste refletirá no próximo ciclo.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro");
    } finally {
      setAdding(false);
    }
  };

  const startCheckout = async (form) => {
    setCheckoutSubmitting(true);
    try {
      // Stripe responds in <1s so we skip the async-polling pattern
      // we needed for Asaas. Backend returns the checkout URL directly
      // and we redirect the browser to the hosted Stripe Checkout page.
      const { data } = await api.post("/billing/checkout/start", form, {
        timeout: 20000,
      });
      const url = data?.url || data?.link;
      if (!url) throw new Error("Backend não retornou a URL do checkout");
      window.location.href = url;
    } catch (e) {
      const apiDetail = e.response?.data?.detail;
      const errorField = e.response?.data?.error;
      const msg = (apiDetail && formatApiError(apiDetail))
        || errorField
        || e.message
        || "Erro ao iniciar checkout. Tente de novo em alguns segundos.";
      toast.error(msg, { duration: 8000 });
      setCheckoutSubmitting(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm(
      "Enviaremos um e-mail para confirmar o cancelamento. Nada será cancelado até você clicar no link do e-mail. Continuar?"
    )) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(
        data?.email
          ? `E-mail de confirmação enviado para ${data.email}. Verifique sua caixa de entrada (link válido por 60 min).`
          : "E-mail de confirmação enviado. Verifique sua caixa de entrada.",
        { duration: 8000 }
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao solicitar cancelamento");
    } finally {
      setCancelling(false);
    }
  };

  if (loading || !sub) {
    return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>;
  }

  const daysLeft = sub.trial_ends_at ? Math.ceil((new Date(sub.trial_ends_at) - new Date()) / 86400000) : null;
  const hasActiveSubscription = sub.is_paid && sub.status === "active";
  const shouldOfferSubscribe = !sub.is_lifetime && !hasActiveSubscription && !sub.is_cancelled_grace;
  const canBuyExtras = hasActiveSubscription && !sub.is_cancelled_grace;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="my-billing-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Minha Conta</div>
        <h1 className="text-3xl font-black tracking-tighter">Assinatura Vyntric</h1>
        <p className="text-sm text-slate-600 mt-1">
          Plano atual, uso de recursos e opções de expansão.
        </p>
      </div>

      {/* Trial banner */}
      {sub.trial_active && daysLeft !== null && (
        <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-4 flex items-start gap-3" data-testid="trial-banner">
          <TrendingUp className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-black text-amber-900">
              Você está em período de trial — {daysLeft} {daysLeft === 1 ? "dia restante" : "dias restantes"}
            </div>
            <div className="text-xs text-amber-800 mt-1">
              Após o trial, sua assinatura de {brl(sub.monthly_total_brl)}/mês será cobrada.
              Para continuar sem interrupção, configure seu pagamento agora.
            </div>
          </div>
          <button
            onClick={() => setCheckoutOpen(true)}
            className="px-3 py-2 text-xs font-black uppercase tracking-wider border-2 border-amber-700 bg-amber-700 text-white hover:bg-amber-800 rounded-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid="trial-cta-subscribe"
          >
            <CreditCard className="w-3.5 h-3.5" />
            Assinar agora
          </button>
        </div>
      )}

      {!sub.can_use && (
        <div className="border-2 border-red-300 bg-red-50 rounded-xl p-4 flex items-start gap-3" data-testid="expired-banner">
          <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-black text-red-900">Assinatura expirada</div>
            <div className="text-xs text-red-800 mt-1">
              Regularize seu pagamento para voltar a usar o Vyntric.
            </div>
          </div>
          <button
            onClick={() => setCheckoutOpen(true)}
            className="px-3 py-2 text-xs font-black uppercase tracking-wider border-2 border-red-700 bg-red-700 text-white hover:bg-red-800 rounded-sm inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid="expired-cta-subscribe"
          >
            <CreditCard className="w-3.5 h-3.5" />
            Regularizar
          </button>
        </div>
      )}

      {/* Plan card */}
      <div className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-xl p-6" data-testid="plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest font-black bg-emerald-600 text-white rounded-sm">
              <Zap className="w-3 h-3" /> Plano {sub.plan_code === "base" ? "Vyntric" : sub.plan_code}
            </div>
            <div className="mt-3 text-4xl font-black tracking-tighter">
              {brl(sub.monthly_total_brl)}
              <span className="text-sm text-slate-500 font-normal">/mês</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">
              {brl(sub.base_price_brl)} base + {sub.extra_blings} Bling extra × {brl(sub.extra_bling_brl)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Status</div>
            <div className={`text-lg font-black ${sub.is_cancelled_grace ? "text-amber-700" : "text-emerald-700"}`} data-testid="subscription-status">
              {sub.is_lifetime
                ? "LIFETIME"
                : sub.trial_active
                ? "TRIAL"
                : sub.is_cancelled_grace
                ? "CANCELADO"
                : sub.is_paid
                ? "ATIVO"
                : "EXPIRADO"}
            </div>
            {sub.paid_until && (
              <div className={`text-xs mt-1 ${sub.is_cancelled_grace ? "text-amber-700 font-bold" : "text-slate-500"}`}>
                {sub.is_cancelled_grace ? "acesso até " : "até "}
                {new Date(sub.paid_until).toLocaleDateString("pt-BR")}
              </div>
            )}
          </div>
        </div>

        {sub.is_cancelled_grace && (
          <div className="mt-4 px-4 py-3 border border-amber-200 bg-amber-50 text-xs text-amber-900 leading-relaxed" data-testid="cancelled-banner">
            <b>Assinatura cancelada.</b> Nenhuma nova cobrança será feita.
            Você continua com acesso completo até{" "}
            <b>{new Date(sub.paid_until).toLocaleDateString("pt-BR")}</b>.
            Se mudar de ideia, clique em <b>Reativar</b> abaixo para voltar
            ao plano — nenhum dado é perdido.
          </div>
        )}

        <div className="mt-5 border-t border-emerald-200 pt-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-slate-600">Uso de Blings</div>
              <div className="text-2xl font-black text-slate-900 mt-0.5">
                {sub.used_blings} <span className="text-slate-400 font-normal">/ {sub.bling_limit}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {shouldOfferSubscribe && (
                <button
                  data-testid="cta-subscribe"
                  onClick={() => setCheckoutOpen(true)}
                  className="px-4 py-2.5 text-xs font-black uppercase tracking-wider border-2 border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 rounded-sm inline-flex items-center gap-2"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  Assinar plano
                </button>
              )}
              <button
                data-testid="buy-extra-bling"
                onClick={buyExtraBling}
                disabled={adding || !canBuyExtras}
                className="px-4 py-2.5 text-xs font-black uppercase tracking-wider border-2 border-emerald-600 bg-white hover:bg-emerald-50 text-emerald-700 rounded-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canBuyExtras ? "Disponível apenas para assinaturas ativas" : ""}
              >
                {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Comprar Bling extra
                <span className="font-mono text-emerald-600">+{brl(sub.extra_bling_brl)}</span>
              </button>
            </div>
          </div>
          <div className="w-full h-2 bg-emerald-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${Math.min(100, (sub.used_blings / Math.max(1, sub.bling_limit)) * 100)}%` }}
            />
          </div>
        </div>

        {hasActiveSubscription && (
          <div className="mt-4 pt-3 border-t border-emerald-100 flex justify-end">
            <button
              onClick={cancelSubscription}
              disabled={cancelling}
              className="text-xs font-black uppercase tracking-wider text-slate-500 hover:text-red-700 inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="cancel-subscription"
            >
              {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
              Cancelar assinatura
            </button>
          </div>
        )}
      </div>

      {/* Features */}
      <div className="border border-slate-200 bg-white rounded-xl p-6">
        <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-3">
          Recursos do seu plano
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            "Faturamento automático 24/7",
            "Descontos por marketplace",
            "Retry inteligente (SEFAZ)",
            "Histórico de 90 dias",
            "Dashboard em tempo real",
            "Multi-marketplace",
            "Multi-usuário na conta",
            "Suporte via e-mail",
          ].map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              {f}
            </div>
          ))}
        </div>
      </div>

      <div className="text-xs text-slate-500 text-center py-4">
        Pagamentos processados de forma segura via Stripe.
        {" "}Cartão de crédito · Assinatura mensal · Cancele quando quiser.
      </div>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onSubmit={startCheckout}
        submitting={checkoutSubmitting}
        defaults={{ name: sub.account_name, email: undefined }}
        basePriceBrl={sub.base_price_brl}
        extraBlingBrl={sub.extra_bling_brl}
      />
    </div>
  );
}
